'use client'

import Link from 'next/link'
import { useCallback } from 'react'
import { useRouter } from 'next/navigation'

import { LEARN_BASE_PATH } from '@/utilities/learnBasePath'
import type { Learn, LearnCategory } from '@/payload-types'

interface Props {
  article: Learn
  searchQuery?: string
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-civo-lime text-black font-medium px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

function getMatchText(article: Learn, query: string): string {
  const snippet = article.snippet || article.meta?.description || ''
  const lowerQuery = query.toLowerCase()

  // Find the best text source that contains the query
  const source = [snippet, article.title].find(
    (t) => t && t.toLowerCase().includes(lowerQuery),
  ) || snippet || article.title

  // Centre the match within a window so it's visible within the line clamp
  const matchIndex = source.toLowerCase().indexOf(lowerQuery)
  if (matchIndex <= 0) return source

  const windowChars = 120
  const idealStart = Math.max(0, matchIndex - Math.floor((windowChars - query.length) / 2))

  if (idealStart === 0) return source

  const start = source.lastIndexOf(' ', idealStart)
  const trimmed = source.slice(start > 0 ? start + 1 : idealStart)
  return '...' + trimmed
}

export function LearnCard({ article, searchQuery }: Props) {
  const router = useRouter()
  const articleHref = `${LEARN_BASE_PATH}/${article.slug}`

  const publishedDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  const categories = (article.categories || [])
    .filter((cat): cat is LearnCategory => typeof cat === 'object')
  const visibleCategories = categories.slice(0, 2)
  const hiddenCount = Math.max(0, categories.length - 2)

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    if (window.getSelection()?.toString()) return
    if ((e.target as HTMLElement).closest('a')) return
    router.push(articleHref)
  }, [articleHref, router])

  return (
    <div
      onClick={handleCardClick}
      className="group flex flex-col gap-3 justify-between bg-civo-cloud/50 border border-transparent rounded-[6px] p-4 lg:p-6 cursor-pointer transition-all duration-300 hover:bg-civo-cloud hover:border-black/10 hover:shadow-[0px_1px_3px_rgba(0,0,0,0.05),0px_1px_2px_rgba(0,0,0,0.05)]"
    >
      <div className="flex flex-col gap-3">
        {publishedDate && (
          <time dateTime={article.publishedAt!} className="body-xsmall font-medium text-black/70">
            {publishedDate}
          </time>
        )}

        <div className="flex flex-col gap-1.5">
          <h3 className="subheading-small mb-0">
            <Link href={articleHref} className="text-black no-underline line-clamp-3">
              {searchQuery ? <HighlightMatch text={article.title} query={searchQuery} /> : article.title}
            </Link>
          </h3>

          {searchQuery && (
            <p className="body-small text-black/70 mb-0 line-clamp-2">
              <HighlightMatch text={getMatchText(article, searchQuery)} query={searchQuery} />
            </p>
          )}
        </div>
      </div>

      {visibleCategories.length > 0 && (
        <div className="flex items-center gap-3 flex-nowrap overflow-hidden border-t border-civo-cloud group-hover:border-black/20 pt-4 transition-colors duration-300">
          {visibleCategories.map((cat) => (
            <Link
              key={cat.id}
              href={`${LEARN_BASE_PATH}/categories/${cat.slug}`}
              className="bg-civo-cloud px-1.5 py-1 rounded-[4px] body-small font-medium text-black border border-transparent truncate min-w-0 transition-all duration-300 hover:underline group-hover:bg-civo-cloud-dark group-hover:border-black/10"
            >
              {cat.title}
            </Link>
          ))}
          {hiddenCount > 0 && (
            <span className="body-xsmall font-medium text-black shrink-0 whitespace-nowrap">
              + {hiddenCount} more
            </span>
          )}
        </div>
      )}
    </div>
  )
}
